import { makeAutoObservable, runInAction } from 'mobx';
import cloneDeep from 'lodash.clonedeep';

import { AuthStore } from 'stores/users/AuthStore';
import { LoadStatus } from 'utils/types';
import { serverUri } from 'utils/config';

import {
  DiligenceAccessLevel,
  SharedAccessInput,
  SharedWithUser,
  UserPreviewOutput,
} from 'dtos/diligence/DiligenceAccess';
import { nullsToUndefined } from 'utils/nullsToUndefined';
import { diligenceAccessLevelOptions } from 'utils/options';

export class CancelShareEvent extends Event {
  static readonly eventType = 'cancelShare';
  constructor() {
    super(CancelShareEvent.eventType);
  }
}

export class ShareAccessEvent extends Event {
  static readonly eventType = 'shareAccess';
  constructor() {
    super(ShareAccessEvent.eventType);
  }
}

export class DiligenceAccessStore {
  readonly events = new EventTarget();

  closing = false;
  saveStatus: LoadStatus = 'none';
  usersPreviewsStatus: LoadStatus = 'none';
  usersPreviews: UserPreviewOutput[] = [];
  sharedWith: SharedWithUser[] = [];
  setSharedWith: (sharedWith: SharedWithUser[]) => void;

  constructor(
    private readonly token: string,
    private readonly authStore: AuthStore,
    sharedWith: SharedWithUser[] = [],
    setSharedWith: (sharedWith: SharedWithUser[]) => void = () => {}
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.saveStatus = 'none';
    this.closing = false;
    this.sharedWith = sharedWith ? cloneDeep(sharedWith) : [];
    this.setSharedWith = setSharedWith;

    this.getUsersPreviews();
  }

  async getUsersPreviews() {
    this.usersPreviewsStatus = 'loading';
    this.usersPreviews = [];

    try {
      const response = await this.authStore.fetchWithAuth(
        `${serverUri}/diligence/${encodeURIComponent(this.token)}/users`,
        {
          method: 'GET',
        }
      );

      if (!response.ok) throw new Error('Error getting accounts');

      const output: UserPreviewOutput[] = (await response.json())?.users || [];
      runInAction(() => {
        this.usersPreviewsStatus = 'success';
        this.usersPreviews = output;
      });
    } catch (err) {
      runInAction(() => {
        this.usersPreviewsStatus = 'error';
      });
    }
  }

  startClosing = () => {
    this.closing = true;
  };

  handleClosed = () => {
    this.closeCallback();
  };

  shareAccessWith(user: UserPreviewOutput) {
    const isAlreadyShared = this.sharedWith.some(({ email }) => email?.toLowerCase() === user.email?.toLowerCase());
    if (!isAlreadyShared) {
      this.sharedWith.push({ ...user, accessLevel: 'diligence' });
    }
  }

  changeUserAccessLevel(email: string, accessLevel: DiligenceAccessLevel) {
    if (!accessLevel || !Object.keys(diligenceAccessLevelOptions).includes(accessLevel)) {
      return;
    }

    const targetUserIndex = this.sharedWith.findIndex((user) => email?.toLowerCase() === user.email?.toLowerCase());
    if (targetUserIndex !== -1) {
      this.sharedWith[targetUserIndex] = { ...this.sharedWith[targetUserIndex], accessLevel };
    }
  }

  private closeCallback = () => {
    this.events.dispatchEvent(new CancelShareEvent());
  };

  saveModal = async () => {
    this.saveStatus = 'loading';
    const dto: SharedAccessInput = {
      sharedWith: this.sharedWith
        .filter(({ accessLevel }) => accessLevel !== 'removeAccess')
        .map(({ email, accessLevel }) => ({ email, accessLevel })),
    };
    try {
      const response = await this.authStore.fetchWithAuth(
        `${serverUri}/diligence/${encodeURIComponent(this.token)}/share`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dto),
        }
      );

      if (!response.ok) {
        throw new Error();
      }

      const sharedWith: SharedWithUser[] = nullsToUndefined(await response.json());
      runInAction(() => {
        this.saveStatus = 'success';
        this.setSharedWith?.(sharedWith);
        this.closeCallback = () => {
          this.events.dispatchEvent(new ShareAccessEvent());
        };
      });
      this.startClosing();
    } catch (err) {
      console.error(err);
      runInAction(() => {
        this.saveStatus = 'error';
      });
    }
  };
}
